import posthog from "posthog-js";
import { registerPostHogFirstTouch } from "@/lib/posthog-first-touch";
import { isPostHogBrowserCaptureEnabled } from "@/lib/posthog-environment";
import {
	isPostHogSuppressedPath,
	isRecoveryPath,
	containsRecoveryCapability,
} from "@/lib/posthog-suppressed-path";
import { DEFAULT_POSTHOG_HOST } from "@/config/defaults";
import { createPostHogNoiseClassifier } from "@/lib/posthog-noise";

// `||` (not `??`): build-time-baked NEXT_PUBLIC_* may end up as the literal
// empty string when the build arg is absent, and `??` would let "" through —
// PostHog with api_host:"" then sends every call to the page origin (localhost),
// produces 404s/MIME mismatches, mutates the DOM before React hydrates, and
// triggers React error #418.
const key = process.env.NEXT_PUBLIC_POSTHOG_KEY || process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || DEFAULT_POSTHOG_HOST;

// DEBUG: Bypass switch to test if PostHog is the source of hydration mismatch.
// Set NEXT_PUBLIC_DISABLE_POSTHOG=1 in .env.local (or build arg) to skip init.
const disablePostHog = process.env.NEXT_PUBLIC_DISABLE_POSTHOG === "1";
const release = process.env.NEXT_PUBLIC_POSTHOG_RELEASE?.trim();
const postHogEnabled = isPostHogBrowserCaptureEnabled({
	nodeEnv: process.env.NODE_ENV,
	disablePostHog,
	hostname: typeof window === "undefined" ? undefined : window.location.hostname,
});

// Capability URLs must not reach SDK initialization, persistence or feature-flag requests.
const recoveryEntry = typeof window !== "undefined" && isRecoveryPath(window.location.pathname);
if (key && postHogEnabled && !recoveryEntry) {
	try {
		let storage: Storage | undefined;
		try {
			storage = typeof window !== "undefined" ? window.sessionStorage : undefined;
		} catch {
			/* Optional evidence only. */
		}
		const noise = createPostHogNoiseClassifier(storage);
		if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
			const onInteraction = (event: Event) => noise.recordInteraction(event.isTrusted);
			for (const event of ["pointerdown", "keydown", "touchstart", "click", "wheel"])
				window.addEventListener(event, onInteraction, { capture: true, passive: true });
		}
		posthog.init(key, {
			api_host: apiHost,
			ui_host: "https://us.posthog.com",
			defaults: "2026-05-30",
			person_profiles: "identified_only",
			// The hydrated route tracker owns pageviews and enables autocapture.
			// Explicit false prevents the SDK defaults from sending duplicate pageviews.
			capture_pageview: false,
			capture_pageleave: true,
			autocapture: false,
			capture_performance: { web_vitals: true },
			mask_all_text: true,
			mask_all_element_attributes: true,
			session_recording: { maskAllInputs: true, maskTextSelector: "*" },
			capture_exceptions: true,
			debug: process.env.NODE_ENV === "development",
			before_send: (event) => {
				if (
					!isPostHogBrowserCaptureEnabled({
						nodeEnv: process.env.NODE_ENV,
						disablePostHog,
						hostname: typeof window === "undefined" ? undefined : window.location.hostname,
					})
				)
					return null;
				if (containsRecoveryCapability(event)) return null;
				if (typeof window !== "undefined" && isPostHogSuppressedPath(window.location.pathname)) {
					return null;
				}
				if (!event) return event;
				// Automatic SDK events can precede `loaded`/register. Label at the
				// final send boundary, including exceptions and pageleave events.
				event.properties = {
					...event.properties,
					environment: "production",
					...(release ? { release } : {}),
					...noise.labels(event, {
						url: typeof window === "undefined" ? undefined : window.location.href,
						referrer: typeof document === "undefined" ? undefined : document.referrer,
						userAgent: typeof navigator === "undefined" ? undefined : navigator.userAgent,
					}),
				};
				return event;
			},
			loaded: (client) => {
				if (typeof window !== "undefined") registerPostHogFirstTouch(client, window.location.href);
				client.register({ app: "eurolabs-storefront", environment: process.env.NODE_ENV });
				if (release) {
					try {
						client.register({ release });
					} catch {
						// ignore
					}
				}
				if (typeof window !== "undefined" && isPostHogSuppressedPath(window.location.pathname)) {
					try {
						client.stopSessionRecording();
					} catch {
						// ignore
					}
				}
			},
		});
	} catch {
		// PostHog init must never crash — it runs before React hydration
	}
}
